<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-md-8 mx-auto">
        <div class="card shadow-lg">
          <div class="card-header pb-0 text-center">
            <h6 class="mb-0 text-uppercase">Crear y Finalizar Auto-Gestión</h6>
            <p class="text-xs text-secondary">La tarea se creará y completará automáticamente</p>
          </div>

          <div class="card-body">
            <form @submit.prevent="submitSelfTask">
              <div class="bg-gray-100 p-3 border-radius-lg mb-4">
                <h6 class="text-xs font-weight-bolder text-uppercase mb-3">1. Detalles de la Tarea</h6>

                <div class="row">
                  <div class="col-md-12 mb-3">
                    <label class="form-control-label text-xs">Descripción / Concepto</label>
                    <argon-input
                      v-model="formData.task_description"
                      placeholder="Ej: Visita imprevista a cliente, entrega express..."
                    />
                  </div>

                  <div class="col-md-6 mb-3">
                    <label class="form-control-label text-xs">Tipo de Gestión</label>
                    <select v-model="formData.task_type" class="form-select">
                      <option v-for="type in taskTypes" :key="type.id" :value="type.id">
                        {{ type.name }}
                      </option>
                    </select>
                  </div>

                  <div class="col-md-6 mb-3">
                    <label class="form-control-label text-xs">Prioridad</label>
                    <select v-model="formData.priority" class="form-select">
                      <option value="Low">Baja</option>
                      <option value="Medium">Media</option>
                      <option value="High">Alta</option>
                    </select>
                  </div>

                  <div v-if="formData.task_type == 1 || formData.task_type == 6" class="col-md-12 mb-3">
                    <label class="form-control-label text-xs">Monto Recaudado ($)</label>
                    <input type="number" step="0.01" class="form-control" v-model.number="formData.collected" />
                  </div>
                </div>
              </div>

              <div class="col-md-12 text-center mb-4">
                <label class="form-control-label d-block text-xs font-weight-bolder text-uppercase">2. Captura de Evidencia</label>
                <div class="evidence-upload-area mx-auto" @click="openCamera" :style="evidencePreview ? 'border-style: solid' : ''">
                  <img v-if="evidencePreview" :src="evidencePreview" class="img-fluid border-radius-lg" />
                  <div v-else class="py-4">
                    <i class="fa fa-camera fa-2x text-secondary mb-2"></i>
                    <p class="text-xs mb-0">Tocar para tomar foto</p>
                  </div>
                  <input type="file" ref="cameraInput" accept="image/*" capture="environment" style="display: none" @change="handleFileChange" />
                </div>
                <small v-if="submitted && !formData.evidence_url" class="text-danger text-xxs">La foto es obligatoria.</small>
              </div>

              <div class="col-md-12 mb-3">
                <label class="form-control-label text-xs">Comentarios Adicionales</label>
                <textarea v-model="formData.user_notes" class="form-control" rows="2" placeholder="Notas sobre el resultado..."></textarea>
              </div>

              <div class="col-md-12">
                <div class="d-flex align-items-center bg-light p-2 border-radius-md justify-content-center">
                  <i :class="['fa fa-map-marker-alt me-2', formData.latitude ? 'text-success' : 'text-danger']"></i>
                  <span class="text-xxs font-weight-bold">{{ locationStatus }}</span>
                </div>
              </div>

              <argon-button
                type="submit"
                class="mt-4"
                color="success"
                fullWidth
                size="lg"
                :disabled="isSubmitting"
              >
                <span v-if="isSubmitting">Procesando...</span>
                <span v-else>Guardar Mi Gestión</span>
              </argon-button>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ArgonInput from "@/components/ArgonInput.vue";
import ArgonButton from "@/components/ArgonButton.vue";
import axios from "axios";
import Swal from 'sweetalert2';
import { useUpload } from "@/composables/useUpload";

export default {
  name: "SelfTask",
  components: { ArgonInput, ArgonButton },
  setup() {
    const { uploadFile, uploading } = useUpload();
    return { uploadFile, uploading };
  },
  data() {
    return {
      isSubmitting: false,
      submitted: false,
      evidencePreview: null,
      locationStatus: "Obteniendo ubicación...",
      taskTypes: [],
      formData: {
        company_id: localStorage.getItem('companyId'),
        user_id: localStorage.getItem('userId'), // Quien la crea
        assigned_to: localStorage.getItem('userId'), // Se asigna a sí mismo
        self: 1, // <--- CAMPO CLAVE QUE PEDISTE
        task_description: '',
        task_type: 1,
        priority: 'Medium',
        user_notes: '',
        collected: 0,
        evidence_url: null,
        latitude: null,
        longitude: null
      }
    };
  },
  methods: {
    async fetchTaskTypes() {
      try {
        const token = localStorage.getItem("accessToken");
        const res = await axios.get(`${this.$store.state.apiURL}task_type`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        this.taskTypes = res.data;
      } catch (e) { console.error("Error loading types"); }
    },

    getLocation() {
      if (!navigator.geolocation) {
        this.locationStatus = "GPS no soportado";
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          this.formData.latitude = pos.coords.latitude;
          this.formData.longitude = pos.coords.longitude;
          this.locationStatus = "📍 Ubicación capturada con éxito";
        },
        () => { this.locationStatus = "⚠️ Error al obtener GPS"; },
        { enableHighAccuracy: true }
      );
    },

    openCamera() { this.$refs.cameraInput.click(); },

    async handleFileChange(event) {
      const file = event.target.files[0];
      if (!file) return;
      const compressedFile = await this.compressImage(file, {
        maxWidth: 1024,
        quality: 0.7
      });
      this.evidencePreview = URL.createObjectURL(compressedFile);

      try {
        const url = await this.uploadFile(compressedFile);
        if (url) {
          this.formData.evidence_url = url;
        }
      } catch (e) {
        console.log(e)
        Swal.fire('Error', 'No se pudo subir la imagen', 'error');
      }
    },

    async submitSelfTask() {
      this.submitted = true;
      if (!this.formData.task_description || !this.formData.evidence_url) {
        Swal.fire('Campos requeridos', 'Completa la descripción y la foto', 'warning');
        return;
      }

      this.isSubmitting = true;
      const token = localStorage.getItem("accessToken");

      try {
        // Este endpoint debe manejar la creación + finalización en una sola transacción
        await axios.post(`${this.$store.state.apiURL}task/self-complete`, this.formData, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        Swal.fire('Éxito', 'Auto-gestión guardada', 'success');
        this.$router.push('/tasks/my');
      } catch (e) {
        Swal.fire('Error', 'No se pudo procesar la tarea', 'error');
      } finally {
        this.isSubmitting = false;
      }
    },
    compressImage(file, { maxWidth, quality }) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (event) => {
          const img = new Image();
          img.src = event.target.result;
          img.onload = () => {
            const canvas = document.createElement('canvas');
            let width = img.width;
            let height = img.height;

            // Calcular nuevas dimensiones manteniendo aspecto
            if (width > maxWidth) {
              height = (maxWidth / width) * height;
              width = maxWidth;
            }

            canvas.width = width;
            canvas.height = height;

            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);

            // Convertir a Blob (formato JPG para mejor compresión)
            canvas.toBlob((blob) => {
              if (blob) {
                // Convertir blob de nuevo a File para que el backend lo reciba igual
                const compressedFile = new File([blob], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now(),
                });
                resolve(compressedFile);
              } else {
                reject(new Error('Canvas toBlob failed'));
              }
            }, 'image/jpeg', quality);
          };
          img.onerror = (err) => reject(err);
        };
        reader.onerror = (err) => reject(err);
      });
    },
  },
  mounted() {
    this.fetchTaskTypes();
    this.getLocation();
  }
};
</script>

<style scoped>
.evidence-upload-area {
  width: 100%;
  max-width: 200px;
  min-height: 150px;
  border: 2px dashed #dee2e6;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background-color: #f8f9fa;
  overflow: hidden;
}
</style>